import { useState } from 'react';
import {
  Mail,
  ShieldOff,
  Shield,
  LogOut,
  RefreshCw,
  UserCheck,
  UserX,
  ArrowRightLeft,
} from 'lucide-react';
import apiService from '../services/api';

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const LivreurAccountPanel = ({ livreur, allLivreurs = [], onUpdated, onError }) => {
  const [busy, setBusy] = useState('');
  const [newLivreurId, setNewLivreurId] = useState('');

  const otherLivreurs = allLivreurs.filter(
    (l) => l.id !== livreur.id && l.actif && l.compteActive
  );

  const runAction = async (key, action) => {
    setBusy(key);
    try {
      await action();
      if (onUpdated) await onUpdated();
    } catch (err) {
      if (onError) onError(err.message || 'Action impossible');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="border-t pt-4 space-y-4">
      <h3 className="text-sm font-semibold text-gray-900">Compte PWA livreur</h3>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
        <div className="rounded-lg bg-gray-50 p-3">
          <p className="text-gray-500 text-xs">Activation</p>
          <p className={`font-medium ${livreur.compteActive ? 'text-green-700' : 'text-amber-700'}`}>
            {livreur.compteActive ? 'Compte activé' : 'En attente d\'activation'}
          </p>
        </div>
        <div className="rounded-lg bg-gray-50 p-3">
          <p className="text-gray-500 text-xs">Livraison</p>
          <p className={`font-medium ${livreur.enLivraison ? 'text-orange-700' : 'text-gray-700'}`}>
            {livreur.enLivraison ? 'En livraison' : 'Disponible / hors course'}
          </p>
        </div>
        <div className="rounded-lg bg-gray-50 p-3">
          <p className="text-gray-500 text-xs">Dernière connexion</p>
          <p className="font-medium text-gray-800">{formatDate(livreur.derniereConnexion)}</p>
        </div>
      </div>

      <p className="text-xs text-gray-500">
        Le mot de passe est défini uniquement par le livreur via l&apos;email d&apos;activation. L&apos;admin ne peut ni le voir ni le modifier.
      </p>

      <div className="flex flex-wrap gap-2">
        {!livreur.compteActive && livreur.actif && (
          <button
            type="button"
            disabled={!!busy}
            onClick={() =>
              runAction('activation', () => apiService.renvoyerActivationLivreur(livreur.id))
            }
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-blue-200 text-blue-700 hover:bg-blue-50 disabled:opacity-50"
          >
            <Mail className="w-4 h-4" />
            {busy === 'activation' ? 'Envoi…' : 'Renvoyer activation'}
          </button>
        )}

        <button
          type="button"
          disabled={!!busy}
          onClick={() =>
            runAction('toggle', () => apiService.setLivreurActif(livreur.id, !livreur.actif))
          }
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border disabled:opacity-50 ${
            livreur.actif
              ? 'border-red-200 text-red-700 hover:bg-red-50'
              : 'border-green-200 text-green-700 hover:bg-green-50'
          }`}
        >
          {livreur.actif ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
          {busy === 'toggle' ? '…' : livreur.actif ? 'Désactiver' : 'Réactiver'}
        </button>

        <button
          type="button"
          disabled={!!busy || !livreur.compteActive}
          onClick={() =>
            runAction('revoke', () => apiService.revoquerSessionsLivreur(livreur.id))
          }
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <ShieldOff className="w-4 h-4" />
          {busy === 'revoke' ? '…' : 'Révoquer sessions'}
        </button>

        {livreur.commandeEnCoursId && (
          <button
            type="button"
            disabled={!!busy}
            onClick={() => runAction('liberer', () => apiService.libererLivreur(livreur.id))}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-amber-200 text-amber-800 hover:bg-amber-50 disabled:opacity-50"
          >
            <LogOut className="w-4 h-4" />
            {busy === 'liberer' ? '…' : 'Libérer'}
          </button>
        )}
      </div>

      {livreur.commandeEnCoursId && otherLivreurs.length > 0 && (
        <div className="rounded-lg border border-gray-200 p-3 space-y-2">
          <p className="text-sm font-medium text-gray-800 flex items-center gap-1">
            <ArrowRightLeft className="w-4 h-4" />
            Réassigner la commande en cours
          </p>
          <div className="flex flex-wrap gap-2 items-center">
            <select
              value={newLivreurId}
              onChange={(e) => setNewLivreurId(e.target.value)}
              className="flex-1 min-w-[180px] px-3 py-1.5 text-sm border border-gray-300 rounded-md"
            >
              <option value="">Choisir un livreur…</option>
              {otherLivreurs.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.prenom} {l.nom}
                  {!l.disponible ? ' (occupé)' : ''}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={!!busy || !newLivreurId}
              onClick={() =>
                runAction('reassign', () =>
                  apiService.reassignerCommandeLivreur(livreur.id, newLivreurId)
                )
              }
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              <RefreshCw className="w-4 h-4" />
              {busy === 'reassign' ? '…' : 'Réassigner'}
            </button>
          </div>
        </div>
      )}

      {livreur.actif && livreur.compteActive && (
        <div className="flex items-center gap-2 text-xs text-green-700">
          <Shield className="w-3.5 h-3.5" />
          Compte actif — accès PWA autorisé
        </div>
      )}
    </div>
  );
};

export default LivreurAccountPanel;
